/**
 * The instance, and the naming law that maps a sign-in identifier to it
 * (INSTANCE-SEPARATION-REQUIREMENTS R1.2 / R2.1, ADR-0023, GLOSSARY "Naming law").
 *
 * WHY THIS IS PLATFORM LOGIC WITH ONE HOME. "Which backend does this person
 * reach" is decided by the physio app at sign-in, re-checked by the API on
 * every request, and enforced at provisioning — three surfaces, one rule. A
 * rule written three times is a rule that drifts, so ADR-0023 decision 2 pins
 * it to "one pure function with one home in `packages/shared`, used identically
 * by the app and the API". This file is that home; nothing here touches I/O, a
 * clock, or the network.
 *
 * THE LAW IS DELIBERATELY NARROW. It reads the LOCAL PART of the identifier the
 * physio types and sees — never the Supabase `sub`, which stays the system's
 * key for every actor column (ADR-0015, unchanged). A `+test` suffix on the
 * local part means practice (`train`); anything else means real patients
 * (`prod`). `dev` is OUTSIDE the law (DP-5): engineering builds stay
 * single-instance and baked, so this function never returns it and an
 * engineering build handed a `+test` identity refuses it rather than resolving
 * to an instance it does not carry (R3.5).
 */

/**
 * Every environment the platform deploys, as ADR-0018 names them. `dev` is
 * engineering's; `train` is practice; `prod` is real patients. The API's
 * required `AN_INSTANCE` is validated against this exact set (R1.2), and the
 * splash and `GET /api/instance` label it. Nothing indexes this tuple —
 * position is not load-bearing — but keep it deployment-ascending for readers.
 */
export const INSTANCES = ['dev', 'train', 'prod'] as const;

export type Instance = (typeof INSTANCES)[number];

/** Narrow an unknown value to an `Instance` without throwing (for `AN_INSTANCE` parsing). */
export function isInstance(value: unknown): value is Instance {
  return typeof value === 'string' && (INSTANCES as readonly string[]).includes(value);
}

/**
 * The human-facing name of each instance (DP-3). One word (or two) in large
 * type: it is what the splash shows and what `GET /api/instance` returns as
 * `label`. Payal's clinical read may change the wording of the two
 * clinician-facing labels later — that is a value change here, never a
 * mechanism change. Colour and glyph are the physio app's (its theme), not
 * this package's.
 */
export const INSTANCE_LABELS: Record<Instance, string> = {
  dev: 'ENGINEERING',
  train: 'PRACTICE',
  prod: 'REAL PATIENTS',
};

/**
 * The two instances a sign-in identifier can select. `dev` is not here: DP-5
 * keeps engineering builds out of the routing entirely, so the law has no
 * `dev` branch.
 */
export type RoutableInstance = Exclude<Instance, 'dev'>;

/**
 * The suffix on an identifier's local part that marks a practice identity
 * (DP-2: the plus-address form, so invites and password resets for
 * `payal+test@…` are delivered to the clinician's own base mailbox). The word
 * is `test` because that is the word the owner used.
 */
export const PRACTICE_LOCAL_PART_SUFFIX = '+test';

/**
 * Normalise a sign-in identifier the way every surface must before the law — or
 * a credential call — sees it: trim surrounding whitespace, lowercase the whole
 * string. Exported and shared because the app must sign in with exactly the
 * string the law judged, and the API compares the token's `email` claim against
 * the same shape (R2.1, R7.1); each side re-implementing "trim, lowercase" is
 * how the two drift apart.
 */
export function normaliseSignInIdentifier(identifier: string): string {
  return identifier.trim().toLowerCase();
}

/**
 * THE NAMING LAW. Maps a sign-in identifier to the instance it belongs to.
 *
 * The local part is everything before the last `@` (an identifier with no `@`
 * is treated as all local part — defensive; a real identifier always has one).
 * If that local part ends in `+test`, the instance is `train`; otherwise
 * `prod`. The match is exact and anchored to the end of the local part, so
 * `+test2`, `+tester`, and a `+test` sitting in the DOMAIN are all `prod`.
 *
 * Enforced identically at sign-in (R3.2), on every API request (R7.1), and at
 * provisioning (R2.3). `dev` is never returned (DP-5).
 */
export function instanceFromSignInIdentifier(identifier: string): RoutableInstance {
  const normalised = normaliseSignInIdentifier(identifier);
  const atIndex = normalised.lastIndexOf('@');
  const localPart = atIndex === -1 ? normalised : normalised.slice(0, atIndex);
  return localPart.endsWith(PRACTICE_LOCAL_PART_SUFFIX) ? 'train' : 'prod';
}
